// Admin Users API layer — typed wrappers over the LIVE /v3/admin/users contract.
//
// ⚠️ LIVE JSON IS GROUND TRUTH (FACTS PACK §1): the deployed build is ahead of the
// source checkout; shapes below are probe-confirmed against v3-api-dev, not admin.go.
// All timestamps are MICROSECONDS since epoch (FACTS PACK §2).
//
// Depends ONLY on the scaffold's shared surface: apiFetch / ApiError / normalizeCursor.

import { ApiError, apiFetch, normalizeCursor } from '../../lib/api';

// ---- shared sub-shapes ----

export interface UserOnline {
  state?: string;
  stale_hint?: boolean;
}

/** Per-device subscription (FACTS PACK §3b: subscription is nested in each device, NOT top-level). */
export interface DeviceSubscription {
  plan?: string;
  status?: string;
  expired_time?: number; // µs
  auto_renew?: boolean;
  retention_days?: number;
  quality_allowed?: string;
}

export interface UserDevice {
  device_id: string;
  model?: string;
  display_name?: string;
  firmware_version?: string;
  status?: string;
  online?: UserOnline;
  role?: string; // 'owner' | 'shared' etc. — display as-is (FACTS PACK §3c note)
  recording?: { mode?: string; location?: string; quality?: string };
  registered_at?: number; // µs
  updated_at?: number; // µs
  subscription?: DeviceSubscription;
}

export interface RecentEvent {
  /** Capability-encoded event-CLASS code (`0x11..`, restful 00 §19) — NOT unique per
   *  row: same-class events share it. Row identity is (event_ts, correlation_id). */
  event_id: string;
  event_type?: string;
  event_ts?: number; // µs
  correlation_id?: string;
  payload?: Record<string, unknown>;
}

// ---- core user shapes ----

/** Row in the browse list / flat object from ?email= lookup (FACTS PACK §3a). */
export interface AdminUser {
  user_id: string;
  email?: string;
  display_name?: string;
  region?: string;
  role?: string; // 'user' | 'admin' | ...
  status?: string; // 'active' | 'pending_verification' | 'suspended' | 'banned' | ...
  phone_e164?: string;
  phone_verified?: boolean;
  created_at?: number; // µs
  /** 08 §2.1 ecosystem (brand family) the account lives in — same email in a
   *  different family is a DIFFERENT account. Admin browse/lookup/360 only. */
  family_id?: string;
  family_name?: string; // resolved families.name (falls back to the id)
  /** common#24/#79 eco-* attribution (admin paths only). base = 原生態系;
   *  joined + platform_union = 加入的生態系 (union is a flag, not enumerated). */
  base_ecosystem_id?: string;
  base_ecosystem_name?: string;
  joined_ecosystems?: { id: string; name: string }[];
  platform_union?: boolean;
}

/** Customer-360 (FACTS PACK §3b). */
export interface UserDetail {
  user: AdminUser;
  devices: UserDevice[];
  recent_events: RecentEvent[];
}

// ---- browse (dual-mode) ----

export interface UserListPage {
  users: AdminUser[];
  /** null when no more pages (covers both "" and an omitted field — normalizeCursor). */
  nextCursor: string | null;
}

export interface UserListParams {
  region?: string;
  status?: string;
  /** Role set (comma-joined into `role=`, 02 §3.3.1) — server keeps rows whose role is in the set. */
  roles?: string[];
  /** 08 §2.1 auth-pool family (family_id) exact filter. */
  family?: string;
  /** eco-* ecosystem effective-membership filter (原 OR 加入; common#24). */
  ecosystem?: string;
  limit?: number;
  cursor?: string;
}

/**
 * BROWSE mode — GET /v3/admin/users WITHOUT `email`.
 * Returns { data.users[], data.next_cursor }. normalizeCursor collapses ""/absent
 * to null so callers just check `if (page.nextCursor)`.
 */
export async function listUsers(params: UserListParams = {}): Promise<UserListPage> {
  const res = await apiFetch<{ users?: AdminUser[]; next_cursor?: string }>('/v3/admin/users', {
    query: {
      region: params.region,
      status: params.status,
      role: params.roles && params.roles.length > 0 ? params.roles.join(',') : undefined,
      family: params.family || undefined,
      ecosystem: params.ecosystem || undefined,
      limit: params.limit,
      cursor: params.cursor,
    },
  });
  return {
    users: Array.isArray(res.data?.users) ? res.data.users : [],
    nextCursor: normalizeCursor(res.data?.next_cursor),
  };
}

/**
 * LOOKUP mode — GET /v3/admin/users?email=... returns a FLAT user object
 * (NOT wrapped in users[]) — FACTS PACK §3a. Returns null on 404/NOT_FOUND so the
 * caller can render an empty state instead of an error.
 */
export async function lookupUserByEmail(email: string): Promise<AdminUser | null> {
  try {
    const res = await apiFetch<AdminUser>('/v3/admin/users', { query: { email } });
    // Defensive: if a future build wraps the hit in users[], unwrap the first row.
    const data = res.data as AdminUser & { users?: AdminUser[] };
    if (data && Array.isArray(data.users)) return data.users[0] ?? null;
    return data && data.user_id ? data : null;
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.code === 'NOT_FOUND')) return null;
    throw e;
  }
}

// ---- Customer-360 ----

/** GET /v3/admin/users/{id} — profile + per-device subscription + recent events. */
export async function getUserDetail(userId: string): Promise<UserDetail> {
  const res = await apiFetch<{ user?: AdminUser; devices?: UserDevice[]; recent_events?: RecentEvent[] }>(
    `/v3/admin/users/${encodeURIComponent(userId)}`
  );
  const d = res.data ?? {};
  return {
    user: (d.user ?? { user_id: userId }) as AdminUser,
    devices: Array.isArray(d.devices) ? d.devices : [],
    recent_events: Array.isArray(d.recent_events) ? d.recent_events : [],
  };
}

// ---- lifecycle actions ----

export type UserAction = 'suspend' | 'unsuspend' | 'ban' | 'force-logout' | 'impersonate';
export type PasswordResetChannel = 'email' | 'sms';

export interface UserActionInput {
  /** Audit context — written to audit_logs.reason / suspended_reason (02 §4.3). */
  reason?: string;
  /** CS/risk case number. REQUIRED by the backend for impersonate: an empty
   *  case_id is rejected with 403 IMPERSONATION_DENIED (service/admin.go). */
  caseId?: string;
}

/**
 * POST /v3/admin/users/{id}/{action}. All are server-side audited (LOCKED rules).
 * suspend / ban / force-logout / impersonate accept a {reason, case_id} body
 * (handler/admin.go adminActionRequest — same shape as device actions); 02 §4.3
 * lists reason as eventually-required, so always pass what the operator entered.
 * unsuspend reads no body — callers omit `input` and none is sent.
 * Returns the raw `data` envelope + the response request_id so callers can echo
 * impersonate tokens and cite the audit request id.
 */
export async function userAction(
  userId: string,
  action: UserAction,
  input: UserActionInput = {}
): Promise<{ data: Record<string, unknown>; requestId: string | null }> {
  const body: Record<string, string> = {};
  if (input.reason) body.reason = input.reason;
  if (input.caseId) body.case_id = input.caseId;
  const res = await apiFetch<Record<string, unknown>>(
    `/v3/admin/users/${encodeURIComponent(userId)}/${action}`,
    { method: 'POST', ...(Object.keys(body).length > 0 ? { body } : {}) }
  );
  return { data: res.data ?? {}, requestId: res.requestId };
}

export async function updateUserPhone(
  userId: string,
  phoneE164: string,
  input: UserActionInput = {}
): Promise<{ data: Record<string, unknown>; requestId: string | null }> {
  const body: Record<string, string> = { phone_e164: phoneE164 };
  if (input.reason) body.reason = input.reason;
  if (input.caseId) body.case_id = input.caseId;
  const res = await apiFetch<Record<string, unknown>>(`/v3/admin/users/${encodeURIComponent(userId)}/phone`, {
    method: 'PATCH',
    body,
  });
  return { data: res.data ?? {}, requestId: res.requestId };
}

export async function sendPasswordReset(
  userId: string,
  channel: PasswordResetChannel,
  input: UserActionInput = {}
): Promise<{ data: Record<string, unknown>; requestId: string | null }> {
  const body: Record<string, string> = { channel };
  if (input.reason) body.reason = input.reason;
  if (input.caseId) body.case_id = input.caseId;
  const res = await apiFetch<Record<string, unknown>>(`/v3/admin/users/${encodeURIComponent(userId)}/reset-password`, {
    method: 'POST',
    body,
  });
  return { data: res.data ?? {}, requestId: res.requestId };
}

// ---- ecosystem re-attribution (restful#84) ----

/** POST /v3/admin/users/{id}/ecosystem 200 body — the user's new 原/加入 生態系. */
export interface UserEcosystemResult {
  user_id: string;
  base_ecosystem_id?: string;
  base_ecosystem_name?: string;
  joined_ecosystems?: { id: string; name: string }[];
  platform_union?: boolean;
  updated_at?: number; // µs
}

/**
 * POST /v3/admin/users/{id}/ecosystem — set (or clear, with an empty ecosystemId)
 * a user's registration/native ecosystem (users.base_ecosystem_id). Operator-gated
 * + audited (reason + case_id required). Returns the resolved 原/加入 attribution
 * so the 360 can refresh without a re-fetch.
 */
export async function setUserEcosystem(
  userId: string,
  ecosystemId: string,
  input: UserActionInput = {}
): Promise<{ data: UserEcosystemResult; requestId: string | null }> {
  const body: Record<string, string> = { ecosystem_id: ecosystemId };
  if (input.reason) body.reason = input.reason;
  if (input.caseId) body.case_id = input.caseId;
  const res = await apiFetch<UserEcosystemResult>(`/v3/admin/users/${encodeURIComponent(userId)}/ecosystem`, {
    method: 'POST',
    body,
  });
  return { data: res.data ?? { user_id: userId }, requestId: res.requestId };
}
